import { ApolloClient } from '@apollo/client'
import { Stripe, StripeElements } from '@stripe/stripe-js'

import User from './User'

export enum PaymentMethodType {
  PAYMENT_METHOD_BANCONTACT = 'PAYMENT_METHOD_BANCONTACT',
  PAYMENT_METHOD_CREDIT_CARD = 'PAYMENT_METHOD_CREDIT_CARD',
  PAYMENT_METHOD_IDEAL = 'PAYMENT_METHOD_IDEAL',
  PAYMENT_METHOD_MULTIBANCO = 'PAYMENT_METHOD_MULTIBANCO',
  PAYMENT_METHOD_PAYPAL = 'PAYMENT_METHOD_PAYPAL',
  PAYMENT_METHOD_SEPA_ONLINE = 'PAYMENT_METHOD_SEPA_ONLINE',
  PAYMENT_METHOD_SOFORT = 'PAYMENT_METHOD_SOFORT',
  PAYMENT_METHOD_EPS = 'PAYMENT_METHOD_EPS',
  PAYMENT_METHOD_GIROPAY = 'PAYMENT_METHOD_GIROPAY',
  PAYMENT_METHOD_TWINT = 'PAYMENT_METHOD_TWINT',
  PAYMENT_METHOD_SATISPAY = 'PAYMENT_METHOD_SATISPAY',
}

// whitelist payment methods that are supported by the client
const supportPaymentMethodTypes = [
  PaymentMethodType.PAYMENT_METHOD_BANCONTACT,
  PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD,
  PaymentMethodType.PAYMENT_METHOD_IDEAL,
  PaymentMethodType.PAYMENT_METHOD_MULTIBANCO,
  PaymentMethodType.PAYMENT_METHOD_PAYPAL,
  PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE,
  PaymentMethodType.PAYMENT_METHOD_SOFORT,
  PaymentMethodType.PAYMENT_METHOD_EPS,
  PaymentMethodType.PAYMENT_METHOD_GIROPAY,
  PaymentMethodType.PAYMENT_METHOD_TWINT,
  PaymentMethodType.PAYMENT_METHOD_SATISPAY,
] as const

export const isSupportedPaymentMethodType = (paymentMethod: { type: PaymentMethodType }) =>
  supportPaymentMethodTypes.includes(paymentMethod.type as (typeof supportPaymentMethodTypes)[number])

export enum PaymentState {
  PAYMENT_STATE_CANCELLED = 'PAYMENT_STATE_CANCELLED',
  PAYMENT_STATE_FAILED = 'PAYMENT_STATE_FAILED',
  PAYMENT_STATE_IN_PROGRESS = 'PAYMENT_STATE_IN_PROGRESS',
  PAYMENT_STATE_ON_HOLD = 'PAYMENT_STATE_ON_HOLD',
  PAYMENT_STATE_OPEN = 'PAYMENT_STATE_OPEN',
  PAYMENT_STATE_SUCCEEDED = 'PAYMENT_STATE_SUCCEEDED',
}

export enum PaymentMethodVerificationMethod {
  PAYMENT_METHOD_IDEAL = 'PAYMENT_METHOD_IDEAL',
  PAYMENT_METHOD_SOFORT = 'PAYMENT_METHOD_SOFORT',
}

export enum CustomPaymentMethodType {
  PAYMENT_METHOD_OFFLINE = 'PAYMENT_METHOD_OFFLINE',
}

export type AdditionalPaymentMethodType = PaymentMethodType | CustomPaymentMethodType

export interface PaymentMethod {
  type: AdditionalPaymentMethodType
  name: string
}

export interface StoredBankAccount {
  __typename: 'StoredBankAccount'
  id: string
  pspId: string
  last4: string
}

export interface StoredBankAccountWithMandates extends StoredBankAccount {
  mandates: Array<{ isVerified: boolean }>
}

export interface Mandate {
  isVerified: boolean
  bankAccount: StoredBankAccount
}

export interface SepaPaymentMethod {
  bankAccountHolder?: string
  pspPaymentMethodId?: string
}

export interface PaymentProvider {
  stripe: Stripe | null
}

export interface PaymentAPI {
  // eslint-disable-next-line @typescript-eslint/ban-types
  apolloClient: ApolloClient<object>
}

export interface PaymentIntent {
  id: string
  clientSecret: string
  amount?: number
  currency?: string
}

export interface PollPaymentStateOptions {
  orderId: string
  lastPaymentAttempt: Date | null
  additionalExpectedPaymentStates?: Array<PaymentState>
}

export interface ConfirmPaymentOptions {
  paymentId: string
  pspPaymentMethodId: string
}

export interface PreparePaymentOptions {
  paymentId: string
  paymentMethodType: PaymentMethodType
}

export interface SetupRecurringPaymentMethodOptions {
  paymentId: string
}

export interface PrepareAndPollPaymentOptions {
  orderId: string
  lastPaymentAttempt: Date
  options: ProcessPaymentOptions
  selectedPaymentMethod: PaymentMethodType
}

export interface ConfirmPaymentAndPollOptions {
  orderId: string
  options: ProcessPaymentOptions
  lastPaymentAttempt: Date
}

export interface SetupRecurringPaymentMethodAndPollOptions {
  orderId: string
  options: ProcessPaymentOptions
  lastPaymentAttempt: Date
}

export interface Payment {
  id: string
  state: PaymentState
  paymentIntent: PaymentIntent | null
  lastAttemptAt: string | null
  canSetupRecurringPaymentMethod: boolean
}

export interface ProcessPaymentOptions {
  user: User
  payment: Payment
  orderId: string
  elements: StripeElements | null
  savedPaymentMethodId?: string
}

export interface PaymentResponse {
  wasSuccessful: boolean
  error?: string
}

export const StripeSourceTypeToPaymentMethod: Record<string, PaymentMethodType> = {
  card: PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD,
  apple_pay: PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD,
  google_pay: PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD,
  sepa_debit: PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE,
  multibanco: PaymentMethodType.PAYMENT_METHOD_MULTIBANCO,
  bancontact: PaymentMethodType.PAYMENT_METHOD_BANCONTACT,
  ideal: PaymentMethodType.PAYMENT_METHOD_IDEAL,
  paypal: PaymentMethodType.PAYMENT_METHOD_PAYPAL,
  sofort: PaymentMethodType.PAYMENT_METHOD_SOFORT,
  eps: PaymentMethodType.PAYMENT_METHOD_EPS,
  giropay: PaymentMethodType.PAYMENT_METHOD_GIROPAY,
  twint: PaymentMethodType.PAYMENT_METHOD_TWINT,
  satispay: PaymentMethodType.PAYMENT_METHOD_SATISPAY,
}

export enum PaymentError {
  PAYMENT_ERROR_REQUEST_TIMEOUT = 'PAYMENT_ERROR_REQUEST_TIMEOUT',
  PAYMENT_ERROR_CANCELLED = 'PAYMENT_ERROR_CANCELLED',
  PAYMENT_ERROR_FAILED = 'PAYMENT_ERROR_FAILED',
  PAYMENT_ERROR_ORDER_ABORTED = 'PAYMENT_ERROR_ORDER_ABORTED',
  PAYMENT_ERROR_STRIPE_SOURCE = 'PAYMENT_ERROR_STRIPE_SOURCE',
}
